import React, { useState, useCallback, useEffect, useMemo } from 'react'
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'

import { KButton } from 'components/core/KButton'
import { KDialog } from 'components/core/KDialog'
import { KDialogCloseButton } from 'components/ordering/KDialogCloseButton'

import { Address, AddressType } from 'models/Address'
import theme from 'core/Theme'

import {
  AddressEditForm,
  AddressParams,
  getAddressLabels,
} from './AddressEditForm'

const actionButtonSx = {
  minWidth: 320,
  height: 52,
  [theme.breakpoints.down('sm')]: {
    minWidth: 160,
    height: 40,
  },
}

export type AddressData = Address

export type AddressEditDialogProps = {
  address?: AddressData
  editable: boolean
  open: boolean
  onClose: () => void
  onSave?: (data: AddressData) => Promise<void>
  onDelete?: (addressId?: string) => Promise<void>
  addressType: AddressType
}

const createFormState = (address?: Address) => ({
  place: undefined,
  address: address?.address ?? '',
  city: address?.city ?? '',
  state: address?.state ?? '',
  zip: address?.zip ?? '',
  country: address?.country ?? '',
  phoneNumber: address?.phone_number ?? '',
  default: address?.default ?? false,
  place_id: address?.place_id ?? '',
})

export const AddressEditDialog = ({
  address,
  editable,
  open,
  onClose,
  onSave,
  onDelete,
  addressType,
}: AddressEditDialogProps) => {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const initialFormState = useMemo(() => createFormState(address), [address])

  const form = useForm<AddressParams>({
    defaultValues: initialFormState,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })

  const {
    handleSubmit,
    clearErrors,
    reset,
    formState: { isDirty },
    getValues,
    trigger,
  } = form

  useEffect(() => {
    if (open) {
      reset(createFormState(address))
    }
  }, [open, address, reset])

  useEffect(() => {
    if (open) {
      const subscription = form.watch(() => {
        // Validation is handled by mode: 'onTouched'
      })
      return () => subscription.unsubscribe()
    }
  }, [form, open])

  const canSubmitForm = useCallback(() => {
    const values = getValues()
    const hasRequiredFields = Boolean(
      values.address && values.city && values.state && values.zip,
    )

    return editable ? isDirty && hasRequiredFields : hasRequiredFields
  }, [editable, isDirty, getValues])

  const handleSaveChanges = useCallback(
    async (data: AddressParams) => {
      if (!canSubmitForm() || !onSave) return

      try {
        setIsSaving(true)
        const addressData: AddressData = {
          id: address?.id,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          country: data.country,
          phone_number: data.phoneNumber,
          default: data.default,
          street: data.street,
          place_id: data.place_id,
        }
        await onSave(addressData)
        onClose()
        reset(createFormState())
      } catch (e) {
        console.error(`Failed to save ${addressType} address:`, e)
      } finally {
        setIsSaving(false)
      }
    },
    [canSubmitForm, onSave, address, addressType, onClose, reset],
  )

  const handleDeleteAddress = useCallback(async () => {
    if (!onDelete) return

    try {
      setIsDeleting(true)
      await onDelete(address?.id)
      reset(createFormState())
      onClose()
    } catch (e) {
      console.error(`Failed to delete ${addressType} address:`, e)
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete, address, addressType, onClose, reset])

  const handleCloseDialog = useCallback(() => {
    clearErrors()
    onClose()
  }, [clearErrors, onClose])

  const handleSubmitClick = useCallback(async () => {
    const isValid = await trigger(['address', 'city', 'state', 'zip'])
    if (isValid) {
      handleSubmit(handleSaveChanges)()
    }
  }, [trigger, handleSubmit, handleSaveChanges])

  const { dialogTitle, actionLabel } = getAddressLabels(addressType, t)

  return (
    <KDialog open={open}>
      <DialogTitle variant="h4" sx={{ paddingX: 0 }}>
        {dialogTitle(editable)}
      </DialogTitle>

      <DialogContent dividers sx={{ paddingX: 0 }}>
        <AddressEditForm form={form} addressType={addressType} />
      </DialogContent>

      <DialogActions
        sx={{ paddingY: 2, paddingX: 0, justifyContent: 'flex-end' }}
      >
        <Grid2
          container
          spacing={2}
          sx={{ justifyContent: 'flex-end', textAlign: 'right' }}
        >
          {editable ? (
            <>
              <Grid2 size={{ md: 6, sm: 6, xs: 12 }}>
                <KButton
                  variant="danger"
                  onClick={handleDeleteAddress}
                  disabled={isDeleting || isSaving}
                  sx={actionButtonSx}
                >
                  {isDeleting ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} color="inherit" />
                      <span>{t('Deleting...')}</span>
                    </Stack>
                  ) : (
                    t('Delete Address')
                  )}
                </KButton>
              </Grid2>

              <Grid2 size={{ md: 6, sm: 6, xs: 12 }}>
                <KButton
                  onClick={handleSubmitClick}
                  disabled={isSaving || !canSubmitForm()}
                  sx={actionButtonSx}
                >
                  {isSaving ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} color="inherit" />
                      <span>{t('Saving...')}</span>
                    </Stack>
                  ) : (
                    <span>{actionLabel(editable)}</span>
                  )}
                </KButton>
              </Grid2>
            </>
          ) : (
            <Grid2 size={{ xs: 12 }}>
              <KButton
                onClick={handleSubmitClick}
                disabled={isSaving || !canSubmitForm()}
                sx={actionButtonSx}
              >
                {isSaving ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <span>{t('Saving...')}</span>
                  </Stack>
                ) : (
                  <span>{actionLabel(editable)}</span>
                )}
              </KButton>
            </Grid2>
          )}
        </Grid2>
      </DialogActions>

      <KDialogCloseButton
        sx={{ width: 36, height: 36 }}
        size="small"
        onClick={handleCloseDialog}
      />
    </KDialog>
  )
}
